import { setAnalyticsCollectionEnabled, setUserId, logEvent } from 'firebase/analytics';
import type { Analytics } from 'firebase/analytics';
import * as Sentry from '@sentry/react';
import {
  COOKIE_CONSENT_STORAGE_KEY,
  COOKIE_CONSENT_SCHEMA_VERSION,
  CONSENT_STORAGE_KEY,
} from '@little-chef/domain';
import type {
  CookieConsentRecord,
  CookieCategoryDecisions,
  ConsentStateValue,
} from '@little-chef/domain';

type ConsentCallback = (record: CookieConsentRecord | null) => void;

class GranularConsentService {
  private readonly analytics: Analytics;
  private currentUid: string | null = null;
  private subscribers: Set<ConsentCallback> = new Set();
  private readonly storageListener: (e: StorageEvent) => void;
  private prevAnalyticsEnabled: boolean = false;
  private isInitialized: boolean = false;

  constructor(analytics: Analytics) {
    this.analytics = analytics;
    this.migrate();
    this.applyToSdk(this.isAnalyticsAccepted());
    this.isInitialized = true;
    this.storageListener = this.handleStorageEvent.bind(this);
    window.addEventListener('storage', this.storageListener);
  }

  getRecord(): CookieConsentRecord | null {
    try {
      const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as CookieConsentRecord;
      if (parsed.version !== COOKIE_CONSENT_SCHEMA_VERSION) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  isAnalyticsAccepted(): boolean {
    const record = this.getRecord();
    return record !== null && record.categories.analytics === true;
  }

  getState(): ConsentStateValue {
    const record = this.getRecord();
    if (record === null) return 'unknown';
    return record.categories.analytics ? 'granted' : 'revoked';
  }

  acceptAll(): void {
    this.writeRecord({ essential: true, analytics: true });
  }

  declineAll(): void {
    this.writeRecord({ essential: true, analytics: false });
  }

  savePreferences(categories: CookieCategoryDecisions): void {
    this.writeRecord({ ...categories, essential: true });
  }

  setCurrentUid(uid: string | null): void {
    this.currentUid = uid;
  }

  subscribe(cb: ConsentCallback): () => void {
    this.subscribers.add(cb);
    cb(this.getRecord());
    return () => {
      this.subscribers.delete(cb);
    };
  }

  destroy(): void {
    window.removeEventListener('storage', this.storageListener);
    this.subscribers.clear();
  }

  private writeRecord(categories: CookieCategoryDecisions): void {
    const record: CookieConsentRecord = {
      version: 1,
      decidedAt: Date.now(),
      categories,
    };
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(record));
    this.applyToSdk(categories.analytics);
    this.notifySubscribers(record);
  }

  private applyToSdk(accepted: boolean): void {
    try {
      setAnalyticsCollectionEnabled(this.analytics, accepted);
      // Fire page_view when transitioning from disabled → enabled mid-session.
      // Skip during constructor init — Firebase fires the automatic page_view itself.
      if (accepted && !this.prevAnalyticsEnabled && this.isInitialized) {
        logEvent(this.analytics, 'page_view', {
          page_location: window.location.href,
          page_title: document.title,
        });
      }
      this.prevAnalyticsEnabled = accepted;
      if (accepted && this.currentUid) {
        setUserId(this.analytics, this.currentUid);
        Sentry.setUser({ id: this.currentUid });
      } else {
        setUserId(this.analytics, null);
        Sentry.setUser(null);
      }
    } catch {
      /* fail-silent */
    }
  }

  private migrate(): void {
    // Skip if new record already exists
    if (localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY) !== null) return;

    const legacy = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (legacy === 'granted' || legacy === 'revoked') {
      const record: CookieConsentRecord = {
        version: 1,
        decidedAt: Date.now(),
        categories: { essential: true, analytics: legacy === 'granted' },
      };
      localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(record));
      localStorage.removeItem(CONSENT_STORAGE_KEY);
    }
  }

  private handleStorageEvent(e: StorageEvent): void {
    if (e.key !== COOKIE_CONSENT_STORAGE_KEY) return;
    let record: CookieConsentRecord | null = null;
    try {
      record = e.newValue ? (JSON.parse(e.newValue) as CookieConsentRecord) : null;
    } catch {
      /* corrupt value — treat as null */
    }
    this.applyToSdk(record?.categories.analytics ?? false);
    this.notifySubscribers(record);
  }

  private notifySubscribers(record: CookieConsentRecord | null): void {
    for (const cb of this.subscribers) {
      try {
        cb(record);
      } catch {
        /* fail-silent */
      }
    }
  }
}

let _instance: GranularConsentService | null = null;

export function getConsentService(analytics?: Analytics): GranularConsentService {
  if (!_instance) {
    if (!analytics) {
      // Return a no-op stub when analytics is not available (e.g. tests without analytics)
      throw new Error(
        'GranularConsentService: analytics instance required for first initialisation',
      );
    }
    _instance = new GranularConsentService(analytics);
  }
  return _instance;
}

/** Reset singleton — for use in tests only. */
export function _resetConsentServiceForTesting(): void {
  if (_instance) {
    _instance.destroy();
    _instance = null;
  }
}
